import React, { FC, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import { currencySelector } from "../../../../redux/selectors/whatsAppCampaign.selector";
import { ISalaryRange } from "../../../../types/campaign.types";
import {
  noticePeriodSelector,
  salaryRangeSelector,
} from "../../../../redux/selectors/campaignSetup.selector";
import { campaignSetupActions } from "../../../../redux/slice/campaignSetUpSlice";
import { COLOR_MAP } from "../../../../constants/ColorNames";
import {
  templateDetailsSelector,
  templatePlaceholdersSelector,
} from "../../../../redux/selectors/campaignCompose.selector";
import { campaignComposeActions } from "../../../../redux/slice/campaignComposeSlice";
import NoticePeriod from "../CampaignSetup/CampaignForm/NoticePeriod";
import CLOSE_ICON from "../../../../constants/icons/CloseIcon";
import SalaryRange from "@foundit/salaryRange/dist/SalaryRange";
import "@foundit/salaryRange/dist/SalaryRange.css";
import { unFormatNumberString } from "../../../../utils/whatsAppUtils";
import { defaultErrorMsg } from "../../../../utils/utils";

interface IProps {
  open: boolean;
  handleClose: () => void;
}

const Heading = styled.span`
  padding: 1.6rem;
  border-bottom: 0.1rem solid ${COLOR_MAP.DISABLED};
  font-weight: 700;
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: ${COLOR_MAP.BLACK};
  display: flex;
`;

const PlaceholdersDialog: FC<IProps> = ({ open, handleClose }) => {
  const [salaryRange, setSalaryRange] = useState<ISalaryRange>({
    min: "",
    max: "",
    currency: "INR",
  });
  const [noticePeriodState, setNoticePeriodState] = useState<string>("");
  const [errors, setErrors] = useState<{ [fieldName: string]: string }>({});

  const { currency } = useSelector(currencySelector);
  const { salaryRange: salaryRangeReduxState } =
    useSelector(salaryRangeSelector);
  const { noticePeriod } = useSelector(noticePeriodSelector);
  const { templatePlaceholders } = useSelector(templatePlaceholdersSelector);
  const { templateDetails } = useSelector(templateDetailsSelector);
  const dispatch = useDispatch();

  const optionalPlaceholders =
    templateDetails?.selectedTemplate?.optionalPlaceholders || [];

  const handleUpdateNoticePeriod = useCallback((newValue: string) => {
    setErrors((prev) => ({
      ...prev,
      noticePeriod: "",
    }));
    setNoticePeriodState(newValue);
  }, []);

  const handleUpdateSalaryRange = (newSalaryRange: ISalaryRange) => {
    setSalaryRange(newSalaryRange);

    if (
      newSalaryRange?.min &&
      newSalaryRange?.max &&
      +unFormatNumberString(newSalaryRange?.min) <=
        +unFormatNumberString(newSalaryRange?.max)
    ) {
      setErrors((prev) => ({
        ...prev,
        salaryRange: "",
      }));
    }
  };

  const handleBlur = () => {
    if (
      salaryRange?.min &&
      salaryRange?.max &&
      +unFormatNumberString(salaryRange?.min) >
        +unFormatNumberString(salaryRange?.max)
    ) {
      setErrors((prev) => ({
        ...prev,
        salaryRange: defaultErrorMsg.salaryRange,
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        salaryRange: "",
      }));
    }
  };

  const handleUpdatePlaceholderValues = () => {
    const errors: { [key: string]: string } = {};

    if (
      (optionalPlaceholders?.includes("@min_salary") ||
        optionalPlaceholders?.includes("@max_salary")) &&
      (!salaryRange?.min || !salaryRange?.max)
    ) {
      errors["salaryRange"] = "Please add complete salary range";
    }

    if (
      optionalPlaceholders?.includes("@notice_period") &&
      !noticePeriodState
    ) {
      errors["noticePeriod"] = "Please enter notice period";
    }

    if (Object.keys(errors).length > 0) {
      setErrors((prev) => ({
        ...prev,
        ...errors,
      }));
      return;
    }

    dispatch(campaignSetupActions.setSalaryRange({ salaryRange }));
    dispatch(
      campaignSetupActions.setNoticePeriod({ noticePeriod: noticePeriodState })
    );

    if (optionalPlaceholders?.length) {
      const optionalPlaceholdersValues: { [key: string]: string } = {};

      optionalPlaceholders?.forEach((item) => {
        switch (item) {
          case "@currency_code":
            optionalPlaceholdersValues[item] = salaryRange.currency;
            break;
          case "@min_salary":
            optionalPlaceholdersValues[item] = salaryRange.min;
            break;
          case "@max_salary":
            optionalPlaceholdersValues[item] = salaryRange.max;
            break;
          case "@notice_period":
            optionalPlaceholdersValues[item] = noticePeriodState;
            break;
          default:
            break;
        }
      });

      dispatch(
        campaignComposeActions.setTemplatePlaceholders({
          placeholders: {
            ...templatePlaceholders,
            ...optionalPlaceholdersValues,
          },
        })
      );
    }

    handleClose();
  };

  useEffect(() => {
    if (salaryRangeReduxState) {
      setSalaryRange(salaryRangeReduxState);
    }
  }, [salaryRangeReduxState]);

  useEffect(() => {
    if (noticePeriod && !noticePeriodState) {
      handleUpdateNoticePeriod(noticePeriod);
    }
  }, [handleUpdateNoticePeriod, noticePeriod, noticePeriodState]);

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      fullWidth
      maxWidth="xs"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Heading>Add the missing placeholder values</Heading>

        <IconButton onClick={handleClose} sx={{ p: 1, mr: 1 }}>
          <CLOSE_ICON />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Salary range  */}
        {(optionalPlaceholders?.includes("@min_salary") ||
          optionalPlaceholders?.includes("@max_salary")) && (
          <Box sx={{ mb: 2 }}>
            <SalaryRange
              handleUpdateSalaryRange={handleUpdateSalaryRange}
              salaryRange={salaryRange}
              currencyList={currency || []}
              isRequired
              error={errors?.salaryRange || ""}
              onBlur={handleBlur}
            />
          </Box>
        )}

        {/* Notice Period  */}
        {optionalPlaceholders?.includes("@notice_period") && (
          <NoticePeriod
            handleUpdateNoticePeriod={handleUpdateNoticePeriod}
            error={errors?.noticePeriod || ""}
            noticePeriod={noticePeriod}
            isRequired
          />
        )}

        <DialogActions sx={{ pb: 0, pt: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              onClick={handleUpdatePlaceholderValues}
              size="small"
              sx={{ textTransform: "none" }}
            >
              Update Placeholders
            </Button>
          </Box>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default PlaceholdersDialog;
